import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import { canCreateMom } from "../../../config/rbac";
import FilterChip from "../../../components/FilterChip";
import Loader from "../../../components/Loader";
import Pagination, { usePagination } from "../../../components/Pagination";
import BulkMomUploadModal from "../../../components/BulkMomUploadModal";
import Toast, { useToast } from "../../../components/Toast";
import EmptyState from "../../../components/EmptyState";
import PageHeader from "../../../components/PageHeader";
import { PRIORITY_COLORS as priorityColors, PRIORITY_BG as priorityBg } from "../../../config/constants";

const PAGE_SIZE = 10;

const emptyPoint = () => ({
  type: "Task",
  title: "",
  ownerUserIds: [],   // supports multiple owners
  dueDate: "",
  priority: "Medium",
  expectedOutcome: "",
  projectId: "",
});


// ── Inline validation banner (modal-only, not the global toast) ───────────────
function ValidationBanner({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div style={{
      padding: "12px 18px", borderRadius: 12, marginBottom: 20,
      fontSize: 13, fontWeight: 500,
      background: isError ? "var(--danger-bg)" : "#f0fdf4",
      border: `1px solid ${isError ? "#fca5a5" : "#86efac"}`,
      color: isError ? "var(--danger-text)" : "var(--success-text)",
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <span>{isError ? "⚠" : "✅"}</span>
      {toast.message}
    </div>
  );
}

// ── Create MOM Modal ──────────────────────────────────────────────────────────
function CreateMomModal({ users, projects, onClose, onCreated }) {
  const [title, setTitle]           = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [notes, setNotes]           = useState("");
  const [points, setPoints]         = useState([emptyPoint()]);
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ title: false, points: [] });

  const showToast = (message, type = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const addPoint    = () => setPoints(p => [...p, emptyPoint()]);
  const removePoint = idx => {
    setPoints(p => p.filter((_, i) => i !== idx));
    setFieldErrors(e => ({ ...e, points: e.points.filter(i => i !== idx).map(i => i > idx ? i - 1 : i) }));
  };
  const updatePoint = (idx, patch) => {
    setPoints(p => p.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
    // Clear error for this point when user edits it
    if (fieldErrors.points.includes(idx)) {
      const updated = { ...patch };
      const point = { ...points[idx], ...updated };
      if (point.title.trim() && point.ownerUserIds?.length > 0)
        setFieldErrors(e => ({ ...e, points: e.points.filter(i => i !== idx) }));
    }
  };

  const handleSubmit = async () => {
    const newErrors = { title: !title.trim(), points: [] };
    points.forEach((p, i) => {
      if (!p.title.trim() || !p.ownerUserIds?.length) newErrors.points.push(i);
    });

    if (newErrors.title || newErrors.points.length > 0) {
      setFieldErrors(newErrors);
      if (newErrors.title) return showToast("Meeting title is required");
      return showToast("Each action point needs a title and at least one owner selected");
    }
    setFieldErrors({ title: false, points: [] });

    try {
      setSaving(true);
      // Expand multi-owner points: one API point per owner
      const expandedPoints = [];
      for (const p of points) {
        if (!p.title.trim() || !p.ownerUserIds?.length) continue;
        for (const ownerId of p.ownerUserIds) {
          expandedPoints.push({
            type: p.type,
            title: p.title.trim(),
            ownerUserId: ownerId,
            dueDate: p.dueDate ? new Date(p.dueDate).toISOString() : null,
            priority: p.priority,
            expectedOutcome: p.expectedOutcome || null,
            projectId: p.projectId || null,
          });
        }
      }
      const payload = {
        title: title.trim(),
        meetingDate: meetingDate ? new Date(meetingDate).toISOString() : null,
        notes: notes || null,
        points: expandedPoints,
      };

      const res = await apiFetch("/api/moms", { method: "POST", body: JSON.stringify(payload) });
      if (!res.ok) {
        const msg = await res.text();
        console.error(msg);
        return showToast("Failed to create MOM — check console for details");
      }
      onCreated(payload.title);
    } catch (e) {
      console.error(e);
      showToast("Failed to create MOM — network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1000 }}>
      <div
        className="glass-card modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 820, width: "95vw", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", padding: 0 }}
      >
        {/* Fixed header — never scrolls */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: "1px solid var(--border)", flexShrink: 0, background: "var(--surface)" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New MOM</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Scrollable body — scrollbar stays inside rounded corners */}
        <div className="modal-scroll-body" style={{ overflowY: "auto", padding: "24px 28px", flex: 1, background: "var(--surface)" }}>
        <ValidationBanner toast={toast} />

        {/* Meeting Details */}
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600 }}>Meeting Details</h3>
          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <label className="form-label">Meeting Title <span style={{ color: "#dc2626" }}>*</span></label>
              <input className="form-input" placeholder="e.g., Weekly Operations Review"
                value={title} disabled={saving}
                onChange={e => { setTitle(e.target.value); if (fieldErrors.title) setFieldErrors(er => ({ ...er, title: false })); }}
                style={fieldErrors.title ? { border: "1.5px solid #dc2626", background: "#fef2f2" } : {}} />
              {fieldErrors.title && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>Meeting title is required</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 16 }}>
              <div>
                <label className="form-label">Meeting Date <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>(Optional)</span></label>
                <input type="date" className="form-input"
                  value={meetingDate} onChange={e => setMeetingDate(e.target.value)} disabled={saving} />
              </div>
              <div>
                <label className="form-label">Notes (Optional)</label>
                <textarea className="form-input" rows={2}
                  value={notes} onChange={e => setNotes(e.target.value)} disabled={saving}
                  style={{ resize: "vertical" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Action Points */}
        <div>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600 }}>Action Points</h3>
          {points.map((p, idx) => {
            const hasPointError = fieldErrors.points.includes(idx);
            const titleMissing = hasPointError && !p.title.trim();
            const ownerMissing = hasPointError && !p.ownerUserIds?.length;
            return (
            <div key={idx} style={{
              borderRadius: 14, padding: 20, marginBottom: 16,
              background: hasPointError ? "#fff8f8" : "var(--surface-2, #f9fafb)",
              border: `1px solid ${hasPointError ? "#fca5a5" : "var(--border, #e5e7eb)"}`,
            }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: hasPointError ? "#dc2626" : "var(--text-2)", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                Point {idx + 1}
                {hasPointError && <span style={{ fontSize: 11, fontWeight: 500, textTransform: "none", color: "#dc2626" }}>— title and owner are required</span>}
              </div>
              <div style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 14 }}>
                  <div>
                    <label className="form-label">Type</label>
                    <select className="form-input" value={p.type} onChange={e => updatePoint(idx, { type: e.target.value })}>
                      <option value="Task">Task</option>
                      <option value="Project">Project</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Title <span style={{ color: "#dc2626" }}>*</span></label>
                    <input className="form-input" placeholder="Describe the action clearly"
                      value={p.title} onChange={e => updatePoint(idx, { title: e.target.value })}
                      style={titleMissing ? { border: "1.5px solid #dc2626", background: "#fef2f2" } : {}} />
                    {titleMissing && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>Title is required</div>}
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 200px 180px", gap: 14 }}>
                  <div>
                    <label className="form-label">Owner(s) <span style={{ color: "#dc2626" }}>*</span> <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>— select one or more</span></label>
                    <select
                      className="form-input"
                      multiple
                      size={Math.min(4, users.length)}
                      value={p.ownerUserIds}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                        updatePoint(idx, { ownerUserIds: selected });
                      }}
                      style={{ height: "auto", ...(ownerMissing ? { border: "1.5px solid #dc2626", background: "#fef2f2" } : {}) }}
                    >
                      {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                    {ownerMissing && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>Select at least one owner</div>}
                    {p.ownerUserIds?.length > 0 && (
                      <div style={{ fontSize: 12, color: "var(--brand)", marginTop: 4 }}>
                        {p.ownerUserIds.length} owner{p.ownerUserIds.length > 1 ? "s" : ""} selected — creates {p.ownerUserIds.length} task{p.ownerUserIds.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Due Date <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>(Optional)</span></label>
                    <input type="date" className="form-input"
                      value={p.dueDate} min={new Date().toLocaleDateString('en-CA')} onChange={e => updatePoint(idx, { dueDate: e.target.value })} />
                  </div>
                  <div>
                    <label className="form-label">Priority</label>
                    <select className="form-input" value={p.priority} onChange={e => updatePoint(idx, { priority: e.target.value })}>
                      <option value="High">🔴 High</option>
                      <option value="Medium">🟡 Medium</option>
                      <option value="Low">🟢 Low</option>
                    </select>
                  </div>
                </div>
                {p.type === "Task" && (
                  <div>
                    <label className="form-label">Link Project (Optional)</label>
                    <select className="form-input" value={p.projectId} onChange={e => updatePoint(idx, { projectId: e.target.value })}>
                      <option value="">(None)</option>
                      {projects.map(proj => <option key={proj.id} value={proj.id}>{proj.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="form-label">Expected Outcome (Optional)</label>
                  <input className="form-input" value={p.expectedOutcome}
                    onChange={e => updatePoint(idx, { expectedOutcome: e.target.value })} />
                </div>
                {points.length > 1 && (
                  <div style={{ textAlign: "right" }}>
                    <button className="btn-ghost" onClick={() => removePoint(idx)}>Remove Point</button>
                  </div>
                )}
              </div>
            </div>
          ); })}

          <div style={{ display: "flex", gap: 12, marginTop: 4, alignItems: "center" }}>
            <button className="btn-ghost" onClick={addPoint}>+ Add Another Point</button>
            <button className="btn" onClick={handleSubmit} disabled={saving}
              style={{ padding: "12px 28px", fontWeight: 600 }}>
              {saving ? "Creating..." : "Create MOM"}
            </button>
          </div>
        </div>
        </div>{/* end scrollable body */}
      </div>
    </div>
  );
}

// ── Task status config ────────────────────────────────────────────────────────
const STATUS_LABEL = {
  Open:             "Open",
  InProgress:       "In Progress",
  Hold:             "On Hold",
  UnderValidation:  "Under Validation",
  Completed:        "Completed",
};
const STATUS_COLOR = {
  Open:             { bg: "#f3f4f6", color: "#4b5563" },
  InProgress:       { bg: "#fff7ed", color: "#c2410c" },
  Hold:             { bg: "#fefce8", color: "var(--warn-text)" },
  UnderValidation:  { bg: "#fdf4ff", color: "#7e22ce" },
  Completed:        { bg: "#f0fdf4", color: "var(--success-text)" },
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MomsPage() {
  const user      = getUser();
  const role      = user?.role || "Staff";
  const canCreate = canCreateMom(role);
  const canDelete = role === "Admin";
  const navigate  = useNavigate();

  const [moms, setMoms]               = useState([]);
  const [users, setUsers]             = useState([]);
  const [projects, setProjects]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState("");
  const [expandedMom, setExpandedMom] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [deletingMomId, setDeletingMomId] = useState(null);
  const [confirmDeleteMom, setConfirmDeleteMom] = useState(null);
  const { toast, showToast, dismissToast } = useToast(4000);
  const [momsPage, setMomsPage]       = useState(1);
  const [search, setSearch]   = useState("");
  const [period, setPeriod]   = useState("");

  const filteredMoms = useMemo(() => {
    let r = moms;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((m) => m.title?.toLowerCase().includes(q));
    }
    if (period) {
      const now = new Date();
      let from, to;
      if (period === "month") {
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to   = now;
      } else if (period === "lastmonth") {
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to   = new Date(now.getFullYear(), now.getMonth(), 0);
      } else if (period === "quarter") {
        from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        to   = now;
      } else if (period === "year") {
        from = new Date(now.getFullYear(), 0, 1);
        to   = now;
      }
      if (from) r = r.filter((m) => m.meetingDate && new Date(m.meetingDate) >= from && new Date(m.meetingDate) <= to);
    }
    return r;
  }, [moms, search, period]);

  const hasFilter = search || period;

  const { totalPages, safePage, paginated } = usePagination(filteredMoms, PAGE_SIZE);
  const currentPage  = safePage(momsPage);
  const paginatedMoms = paginated(currentPage);

  const loadMoms = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res  = await apiFetch("/api/moms");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMoms(Array.isArray(data) ? data.sort((a, b) => new Date(b.meetingDate) - new Date(a.meetingDate)) : []);
    } catch (e) {
      setMoms([]);
      setLoadError(e?.message || "Failed to load meetings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    apiFetch("/api/users/assignable").then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch("/api/projects").then(r => r.json()).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {});
    loadMoms();
  }, [loadMoms]);

  async function deleteMom(mom) {
    setDeletingMomId(mom.id);
    try {
      const res = await apiFetch(`/api/moms/${mom.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
      showToast(`Meeting "${mom.title}" deleted`);
      if (expandedMom === mom.id) setExpandedMom(null);
      await loadMoms();
    } catch (e) {
      showToast(e?.message || "Failed to delete meeting", "error");
    } finally {
      setDeletingMomId(null);
    }
  }

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u.name || u.email])), [users]);

  return (
    <div style={{ maxWidth: 1100 }}>
      <Toast toast={toast} onDismiss={dismissToast} />

      {loadError && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
          {loadError}
        </div>
      )}

      {/* ── Header ── */}
      <PageHeader
        title="Meetings"
        subtitle={loading
          ? "Loading…"
          : hasFilter
            ? `${filteredMoms.length} of ${moms.length} meetings`
            : `${moms.length} meeting${moms.length !== 1 ? "s" : ""}`}
        actions={canCreate && (
          <>
            <button className="btn-ghost" onClick={() => setShowBulk(true)} style={{ padding: "9px 18px", fontWeight: 600 }}>
              ⬆ Bulk Upload
            </button>
            <button className="btn" onClick={() => setShowCreate(true)} style={{ padding: "9px 18px", fontWeight: 600 }}>
              + New MOM
            </button>
          </>
        )}
      />

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center",
        marginBottom: 14, padding: "10px 14px",
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
      }}>
        {/* Search */}
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-3)", pointerEvents: "none" }}>🔍</span>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setMomsPage(1); }}
            placeholder="Search meetings..."
            style={{
              padding: "5px 10px 5px 28px", borderRadius: 20, fontSize: 12, width: 180, outline: "none",
              border: search ? "1.5px solid var(--brand)" : "1px solid var(--border-strong)",
              background: search ? "var(--brand-soft)" : "var(--surface-2)",
              color: "var(--text-1)", transition: "all 0.14s",
            }}
          />
        </div>

        <div style={{ width: 1, height: 18, background: "var(--border-strong)", margin: "0 2px" }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.1em", marginRight: 2 }}>Filter</span>

        {/* Period chip */}
        <FilterChip
          value={period}
          onChange={(v) => { setPeriod(v); setMomsPage(1); }}
          label="Period"
          options={[
            { value: "month",     label: "This Month" },
            { value: "lastmonth", label: "Last Month" },
            { value: "quarter",   label: "Last 3 Months" },
            { value: "year",      label: "This Year" },
          ]}
        />

        {/* Clear */}
        {hasFilter && (
          <>
            <div style={{ width: 1, height: 18, background: "var(--border-strong)", margin: "0 4px" }} />
            <button
              onClick={() => { setSearch(""); setPeriod(""); setMomsPage(1); }}
              style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "var(--danger-bg)", color: "var(--danger-text)", border: "1px solid #fecdd3", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              <span style={{ fontSize: 10 }}>✕</span> Clear
            </button>
          </>
        )}
      </div>

      {/* ── MOM List ── */}
      <div className="glass-card" style={{ overflow: "hidden", padding: 0 }}>
        {loading ? (
          <Loader.TableRows rows={5} />
        ) : filteredMoms.length === 0 ? (
          <EmptyState
            icon="📝"
            title={hasFilter ? "No meetings match your filters." : "No meetings recorded yet."}
            subtitle={!hasFilter ? (canCreate ? 'Click "+ New MOM" to create one.' : "Meetings will appear here once created.") : undefined}
          />
        ) : (
          <>
            <div style={{ display: "grid", gap: 0 }}>
              {paginatedMoms.map((mom, idx) => {
                const isOpen  = expandedMom === mom.id;
                const dateStr = mom.meetingDate
                  ? new Date(mom.meetingDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                  : "—";
                return (
                  <div key={mom.id} style={{
                    borderBottom: idx < paginatedMoms.length - 1 ? "1px solid var(--border, #e5e7eb)" : "none",
                  }}>
                    {/* Row header */}
                    <div className="clickable-row" style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "16px 24px",
                      background: isOpen ? "var(--surface-2, #f9fafb)" : "#fff",
                      userSelect: "none",
                    }}
                      onClick={() => setExpandedMom(isOpen ? null : mom.id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 18 }}>◈</span>
                        <div style={{ minWidth: 0 }}>
                          <div title={mom.title} style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {mom.title}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                            {dateStr} · {mom.points?.length ?? 0} action point{mom.points?.length !== 1 ? "s" : ""}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 12 }}>
                        {canDelete && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteMom(mom); }}
                            disabled={deletingMomId === mom.id}
                            style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 8, border: "1px solid #fecdd3", background: "var(--danger-bg)", color: "var(--danger-text)", cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            {deletingMomId === mom.id ? "Deleting..." : "🗑 Delete"}
                          </button>
                        )}
                        <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                          {isOpen ? "▲" : "▼"}
                        </span>
                      </div>
                    </div>

                    {/* Expanded action points */}
                    {isOpen && (
                      <div style={{ padding: "0 24px 20px", borderTop: "1px solid var(--border, #e5e7eb)" }}>
                        {mom.notes && (
                          <div style={{ padding: "12px 0 4px", fontSize: 13, color: "var(--text-2)", fontStyle: "italic" }}>
                            {mom.notes}
                          </div>
                        )}
                        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                          {(!mom.points || mom.points.length === 0) && (
                            <div style={{ padding: "12px 14px", color: "var(--text-3)", fontSize: 13, textAlign: "center" }}>
                              No action points recorded for this meeting.
                            </div>
                          )}
                          {(mom.points || []).map((pt, i) => {
                            const isTaskDeleted = (pt.type === "Task" && !pt.createdTaskId && !pt.generatedTaskStatus)
                              || pt.generatedTaskStatus === "Deleted";
                            const statusStyle = isTaskDeleted ? null : (STATUS_COLOR[pt.generatedTaskStatus] || null);
                            const isClickable = !!pt.createdTaskId && !isTaskDeleted;
                            return (
                            <div key={pt.id || i}
                              onClick={() => isClickable && navigate(`/tasks/${pt.createdTaskId}`)}
                              style={{
                                display: "grid", gridTemplateColumns: "1fr auto auto auto auto",
                                gap: 12, alignItems: "center",
                                padding: "10px 14px", borderRadius: 10,
                                background: "var(--surface-2, #f9fafb)", fontSize: 13,
                                cursor: isClickable ? "pointer" : "default",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={e => { if (isClickable) e.currentTarget.style.background = "#f0f4ff"; }}
                              onMouseLeave={e => { if (isClickable) e.currentTarget.style.background = "var(--surface-2, #f9fafb)"; }}
                            >
                              <div>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                                  color: pt.type === "Project" ? "#6366f1" : "var(--brand)", marginRight: 6,
                                }}>
                                  {pt.type}
                                </span>
                                <span style={{ fontWeight: 500 }}>{pt.title}</span>
                                {pt.expectedOutcome && (
                                  <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                                    {pt.expectedOutcome}
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: "var(--text-2)", whiteSpace: "nowrap" }}>
                                {userMap[pt.ownerUserId] || "—"}
                              </div>
                              <div style={{ fontSize: 12, color: "var(--text-3)", whiteSpace: "nowrap" }}>
                                {pt.dueDate ? new Date(pt.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
                              </div>
                              <span style={{
                                padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                background: priorityBg[pt.priority] || "#f3f4f6",
                                color: priorityColors[pt.priority] || "#374151",
                              }}>
                                {pt.priority}
                              </span>
                              {isTaskDeleted ? (
                                <span style={{
                                  padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  background: "#f3f4f6", color: "#9ca3af",
                                }}>
                                  Deleted
                                </span>
                              ) : statusStyle ? (
                                <span style={{
                                  padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  background: statusStyle.bg,
                                  color: statusStyle.color,
                                }}>
                                  {STATUS_LABEL[pt.generatedTaskStatus] || pt.generatedTaskStatus}
                                </span>
                              ) : (
                                <span style={{ width: 80 }} />
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Pagination
              page={currentPage} totalPages={totalPages}
              onPageChange={(p) => setMomsPage(safePage(p))} total={filteredMoms.length} pageSize={PAGE_SIZE}
            />
          </>
        )}
      </div>

      {/* ── Modals ── */}
      {showCreate && (
        <CreateMomModal
          users={users} projects={projects}
          onClose={() => setShowCreate(false)}
          onCreated={momTitle => {
            setShowCreate(false);
            showToast(`MOM "${momTitle}" created — tasks and projects have been generated`);
            loadMoms();
          }}
        />
      )}

      {showBulk && (
        <BulkMomUploadModal
          users={users}
          onClose={() => setShowBulk(false)}
          onCreated={(momTitle, count) => {
            setShowBulk(false);
            showToast(`MOM "${momTitle}" created with ${count} action point${count !== 1 ? "s" : ""}`);
            loadMoms();
          }}
        />
      )}

      {confirmDeleteMom && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteMom(null)}>
          <div className="glass-card" style={{ width: 400, padding: 28, borderRadius: 18, boxShadow: "0 25px 60px rgba(0,0,0,0.14)" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 16, fontWeight: 700 }}>Delete Meeting?</h3>
            <p style={{ color: "var(--text-2)", fontSize: 13, marginBottom: 22 }}>
              Delete <strong>{confirmDeleteMom.title}</strong>? The meeting record and all action points will be removed. Tasks created from this meeting will remain but lose their meeting link.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn-ghost" style={{ padding: "9px 16px", borderRadius: 10 }} onClick={() => setConfirmDeleteMom(null)}>Cancel</button>
              <button
                style={{ padding: "9px 16px", borderRadius: 10, fontWeight: 600, background: "var(--danger-bg)", border: "1px solid #fecdd3", color: "var(--danger-text)", cursor: "pointer" }}
                onClick={() => { const m = confirmDeleteMom; setConfirmDeleteMom(null); deleteMom(m); }}
              >
                Delete Meeting
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

